import type { SourceMaterialKind } from '@mira/clients-shared';
import { validateConfirmedSourceText } from './contracts.js';

export type SourceExtractionMethod = 'file-text' | 'pdf-text' | 'tesseract.js' | 'web-speech-recognition';

export interface ExtractedSourceText {
  method: SourceExtractionMethod;
  text: string;
}

export async function extractSourceText(
  file: File,
  kind: SourceMaterialKind,
  onProgress: (progress: number, label: string) => void,
): Promise<ExtractedSourceText> {
  if (kind === 'image') return extractImageText(file, onProgress);
  if (file.type === 'application/pdf') return extractPdfText(file, onProgress);
  onProgress(0.25, 'Reading text');
  const text = validateConfirmedSourceText(await file.text());
  onProgress(1, 'Text extracted');
  return { method: 'file-text', text };
}

async function extractPdfText(file: File, onProgress: (progress: number, label: string) => void): Promise<ExtractedSourceText> {
  onProgress(0.12, 'Opening PDF');
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.min.js';
  const document = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const pages: string[] = [];
  for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
    const page = await document.getPage(pageNumber);
    const content = await page.getTextContent();
    const text = content.items
      .map((item) => 'str' in item && typeof item.str === 'string' ? item.str : '')
      .filter(Boolean)
      .join(' ');
    if (text.trim()) pages.push(text.trim());
    onProgress(0.12 + (pageNumber / document.numPages) * 0.88, `Reading PDF (${pageNumber}/${document.numPages})`);
  }
  if (pages.length === 0) {
    throw new Error('This PDF has no selectable text. Scanned PDFs are not supported in this version.');
  }
  return { method: 'pdf-text', text: validateConfirmedSourceText(pages.join('\n\n')) };
}

async function extractImageText(file: File, onProgress: (progress: number, label: string) => void): Promise<ExtractedSourceText> {
  onProgress(0.02, 'Preparing image text recognition');
  const { createWorker } = await import('tesseract.js');
  const worker = await createWorker(['chi_sim', 'eng'], 1, {
    workerPath: '/assets/tesseract/worker.min.js',
    corePath: '/assets/tesseract/core',
    langPath: '/assets/tesseract/lang',
    logger(message) {
      const progress = typeof message.progress === 'number' ? Math.max(0.02, Math.min(1, message.progress)) : 0.02;
      onProgress(progress, message.status === 'recognizing text' ? 'Recognizing image text' : 'Preparing image text recognition');
    },
  });
  try {
    const result = await worker.recognize(file);
    onProgress(1, 'Image text recognized');
    return { method: 'tesseract.js', text: validateConfirmedSourceText(result.data.text) };
  } finally {
    await worker.terminate();
  }
}
